import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Trash2, Ban, Filter, Search, Edit, CheckSquare, Square, Check, X, FileText, FileX, FilePenLine } from 'lucide-react';
import { AdminLayout, AdminTable, AdminPagination, ConfirmationModal, EventDetailModal } from '../../components/admin';
import { Button, Input, Modal } from '../../components/ui';
import { toast } from '../../components/ui/Toaster';
import { adminEventService as eventService } from '../../services/eventService';
import { adminEventService } from '../../services/adminEventService';
import type { Event } from '../../types';

interface EventsResponse {
  events: Event[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const EventManagementPage: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  
  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Bulk operations
  const [selectedEvents, setSelectedEvents] = useState<Set<number>>(new Set());
  const [bulkOperation, setBulkOperation] = useState<{
    type: 'approve' | 'reject' | null;
    loading: boolean;
  }>({ type: null, loading: false });

  // Bulk depubliceren: reden opgeven (geen window.prompt)
  const [bulkRejectModal, setBulkRejectModal] = useState<{ isOpen: boolean; reason: string }>({
    isOpen: false,
    reason: '',
  });

  // Delete modal — 'cancel' = soft (offline halen, groepen blijven bestaan),
  // 'hard' = definitief verwijderen incl. groepen.
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    event: Event | null;
    mode: 'cancel' | 'hard';
    loading: boolean;
  }>({
    isOpen: false,
    event: null,
    mode: 'cancel',
    loading: false
  });

  // Event Detail Modal
  const [eventDetailModal, setEventDetailModal] = useState<{
    isOpen: boolean;
    eventId: string;
  }>({
    isOpen: false,
    eventId: ''
  });

  useEffect(() => {
    loadEvents();
  }, [pagination.page, pagination.limit, search, statusFilter, typeFilter, sortBy, sortOrder]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      
      const params: any = {
        page: pagination.page,
        limit: pagination.limit,
        sortBy,
        sortOrder
      };
      
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.type = typeFilter;
      
      const response: EventsResponse = await eventService.getAllEvents(params);
      
      setEvents(response.events);
      setPagination(response.pagination);
    } catch (error: any) {
      console.error('Failed to load events:', error);
      toast.error('Fout', 'Kon evenementen niet laden');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handleLimitChange = (limit: number) => {
    setPagination(prev => ({ ...prev, limit, page: 1 }));
  };

  const handleDeleteEvent = async () => {
    if (!deleteModal.event) return;

    const { event, mode } = deleteModal;

    try {
      setDeleteModal(prev => ({ ...prev, loading: true }));

      if (mode === 'hard') {
        // Definitief verwijderen: event + groepen + berichten weg.
        await adminEventService.deleteEventById(event.id);
        toast.success('Succes', 'Evenement definitief verwijderd');
      } else {
        // Annuleren: soft delete, groepen blijven bestaan maar event gaat offline.
        await eventService.deleteEvent(event.id);
        toast.success('Succes', 'Evenement geannuleerd (offline gehaald)');
      }

      setDeleteModal({ isOpen: false, event: null, mode: 'cancel', loading: false });

      // Reload events
      loadEvents();
    } catch (error: any) {
      console.error('Failed to process event:', error);
      const fallback = mode === 'hard' ? 'Kon evenement niet verwijderen' : 'Kon evenement niet annuleren';
      toast.error('Fout', error.response?.data?.message || error.message || fallback);
      setDeleteModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Bulk operations handlers
  const handleSelectEvent = (eventId: number) => {
    const newSelected = new Set(selectedEvents);
    if (newSelected.has(eventId)) {
      newSelected.delete(eventId);
    } else {
      newSelected.add(eventId);
    }
    setSelectedEvents(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedEvents.size === events.length) {
      setSelectedEvents(new Set());
    } else {
      setSelectedEvents(new Set(events.map(event => event.id)));
    }
  };

  const handleBulkApprove = async () => {
    if (selectedEvents.size === 0) return;

    try {
      setBulkOperation({ type: 'approve', loading: true });

      const eventIds = Array.from(selectedEvents);
      const result = await adminEventService.bulkApproveEvents(eventIds);

      toast.success('Succes', `${result.approvedCount} evenementen gemarkeerd als gecontroleerd`);
      setSelectedEvents(new Set());
      loadEvents();
    } catch (error: any) {
      console.error('Failed to bulk approve events:', error);
      toast.error('Fout', error.message || 'Kon evenementen niet markeren als gecontroleerd');
    } finally {
      setBulkOperation({ type: null, loading: false });
    }
  };

  const openBulkRejectModal = () => {
    if (selectedEvents.size === 0) return;
    setBulkRejectModal({ isOpen: true, reason: '' });
  };

  const closeBulkRejectModal = () => {
    if (bulkOperation.loading) return;
    setBulkRejectModal({ isOpen: false, reason: '' });
  };

  const handleBulkReject = async () => {
    if (selectedEvents.size === 0) return;

    try {
      setBulkOperation({ type: 'reject', loading: true });

      const eventIds = Array.from(selectedEvents);
      const result = await adminEventService.bulkRejectEvents(eventIds, bulkRejectModal.reason.trim() || undefined);

      toast.success('Succes', `${result.rejectedCount} evenementen gedepubliceerd`);
      setSelectedEvents(new Set());
      setBulkRejectModal({ isOpen: false, reason: '' });
      loadEvents();
    } catch (error: any) {
      console.error('Failed to bulk reject events:', error);
      toast.error('Fout', error.message || 'Kon evenementen niet depubliceren');
    } finally {
      setBulkOperation({ type: null, loading: false });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    const statusColors = {
      PENDING: 'sf-badge-cyan',
      PUBLISHED: 'sf-badge-lime',
      REJECTED: 'sf-badge-pink',
      CANCELLED: 'sf-badge-pink'
    };

    return (
      <span className={statusColors[status as keyof typeof statusColors] || 'inline-flex items-center rounded-lg bg-white/[.06] px-2.5 py-1 font-mono text-[11px] font-semibold uppercase text-fg-body'}>
        {status}
      </span>
    );
  };

  const getLandingPageStatus = (event: Event) => {
    if (!event.landingPage) {
      return 'none'; // No landing page exists
    }
    
    const hasContent = Boolean(
      event.landingPage.metaDescription?.trim() ||
      event.landingPage.section1Title?.trim() ||
      event.landingPage.section1Content?.trim() ||
      event.landingPage.section2Title?.trim() ||
      event.landingPage.section2Content?.trim() ||
      event.landingPage.section3Title?.trim() ||
      event.landingPage.section3Content?.trim() ||
      event.landingPage.section4Title?.trim() ||
      event.landingPage.section4Content?.trim()
    );
    
    if (event.landingPage.isPublished && hasContent) {
      return 'published'; // Green - published with content
    } else if (hasContent && !event.landingPage.isPublished) {
      return 'draft'; // Orange - has content but not published
    } else {
      return 'empty'; // Red - exists but no meaningful content
    }
  };

  const getLandingPageIcon = (event: Event) => {
    const status = getLandingPageStatus(event);
    
    switch (status) {
      case 'published':
        return { icon: FileText, color: 'text-neon-lime hover:bg-neon-lime/10', title: 'Landing pagina is gepubliceerd' };
      case 'draft':
        return { icon: FilePenLine, color: 'text-warning-500 hover:bg-warning-500/10', title: 'Landing pagina heeft content maar is niet gepubliceerd' };
      case 'empty':
        return { icon: FileX, color: 'text-neon-pink-soft hover:bg-neon-pink/10', title: 'Landing pagina bestaat maar heeft geen content' };
      case 'none':
      default:
        return { icon: FileX, color: 'text-neon-pink-soft hover:bg-neon-pink/10', title: 'Geen landing pagina' };
    }
  };

  const columns = [
    {
      key: 'select',
      title: '',
      render: (_: any, event: Event) => (
        <button
          onClick={() => handleSelectEvent(event.id)}
          className="p-1 hover:bg-white/[.06] rounded transition-colors"
        >
          {selectedEvents.has(event.id) ?
            <CheckSquare className="h-4 w-4 text-neon-cyan" /> :
            <Square className="h-4 w-4 text-fg-faint" />
          }
        </button>
      )
    },
    {
      key: 'title',
      title: 'Evenement',
      sortable: true,
      render: (value: string, event: Event) => (
        <div>
          <div className="font-medium text-fg-bright">{value}</div>
          <div className="text-sm text-fg-muted">
            {event.location} {event.venue && ` • ${event.venue}`}
          </div>
        </div>
      )
    },
    {
      key: 'status',
      title: 'Status',
      sortable: true,
      render: (value: string) => getStatusBadge(value)
    },
    {
      key: 'startDate',
      title: 'Startdatum',
      sortable: true,
      render: (value: string) => formatDate(value)
    },
    {
      key: '_count',
      title: 'Groepen',
      render: (count: any) => count?.groups || 0
    }
  ];

  const renderActions = (event: Event) => {
    const landingPageIconData = getLandingPageIcon(event);
    const LandingPageIcon = landingPageIconData.icon;
    
    return (
      <div className="flex items-center space-x-1">
        <button
          onClick={() => setEventDetailModal({
            isOpen: true,
            eventId: event.id.toString()
          })}
          className="p-2 min-h-[40px] min-w-[40px] rounded-lg text-neon-cyan hover:bg-neon-cyan/10 transition-colors flex items-center justify-center"
          title="Bewerk evenementdetails"
        >
          <Edit className="h-4 w-4" />
        </button>
        <Link
          to={`/admin/events/${event.id}/landing-page`}
          className={`p-2 min-h-[40px] min-w-[40px] rounded-lg ${landingPageIconData.color} transition-colors flex items-center justify-center`}
          title={landingPageIconData.title}
        >
          <LandingPageIcon className="h-4 w-4" />
        </Link>
        <Link
          to={`/events/${event.slug}`}
          className="p-2 min-h-[40px] min-w-[40px] rounded-lg text-neon-violet hover:bg-neon-violet/10 transition-colors flex items-center justify-center"
          title="Bekijk openbare evenement pagina"
        >
          <ExternalLink className="h-4 w-4" />
        </Link>
        <button
          onClick={() => setDeleteModal({ isOpen: true, event, mode: 'cancel', loading: false })}
          className="p-2 min-h-[40px] min-w-[40px] rounded-lg text-warning-500 hover:bg-warning-500/10 transition-colors flex items-center justify-center"
          title="Annuleren (offline halen, groepen blijven bestaan)"
        >
          <Ban className="h-4 w-4" />
        </button>
        <button
          onClick={() => setDeleteModal({ isOpen: true, event, mode: 'hard', loading: false })}
          className="p-2 min-h-[40px] min-w-[40px] rounded-lg text-neon-pink-soft hover:bg-neon-pink/10 transition-colors flex items-center justify-center"
          title="Definitief verwijderen (alles weg incl. groepen)"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    );
  };

  return (
    <AdminLayout 
      title="Evenement beheer" 
      subtitle="Beheer alle evenementen op het platform"
    >
      {/* Filters */}
      <div className="sf-card p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-fg-faint" />
            <Input
              type="text"
              placeholder="Zoek evenementen..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="sf-input py-2.5 text-sm"
          >
            <option value="">Alle statussen</option>
            <option value="PENDING">In behandeling</option>
            <option value="PUBLISHED">Gepubliceerd</option>
            <option value="REJECTED">Afgewezen</option>
            <option value="CANCELLED">Geannuleerd</option>
          </select>
          
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="sf-input py-2.5 text-sm"
          >
            <option value="">Alle types</option>
            <option value="FESTIVAL">Festival</option>
            <option value="CONCERT">Concert</option>
            <option value="CONFERENCE">Conferentie</option>
            <option value="MEETUP">Meetup</option>
            <option value="SPORT">Sport</option>
            <option value="WORKSHOP">Workshop</option>
            <option value="PARTY">Feest</option>
            <option value="OTHER">Anders</option>
          </select>
          
          <Button
            variant="outline"
            onClick={() => {
              setSearch('');
              setStatusFilter('');
              setTypeFilter('');
            }}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters wissen
          </Button>
        </div>
      </div>

      {/* Select All Button */}
      {events.length > 0 && (
        <div className="flex justify-start mb-4">
          <button
            onClick={handleSelectAll}
            className="sf-btn-secondary space-x-2 px-3 py-2 text-sm"
          >
            {selectedEvents.size === events.length ?
              <CheckSquare className="h-4 w-4 text-neon-cyan" /> :
              <Square className="h-4 w-4 text-fg-faint" />
            }
            <span>
              {selectedEvents.size === events.length ? 'Alles deselecteren' : 'Alles selecteren'}
            </span>
          </button>
        </div>
      )}

      {/* Bulk Actions */}
      {selectedEvents.size > 0 && (
        <div className="sf-card-highlight p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-sm font-semibold text-fg-bright">
                {selectedEvents.size} evenement{selectedEvents.size !== 1 ? 'en' : ''} geselecteerd
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleBulkApprove}
                disabled={bulkOperation.loading}
                className="sf-btn-accept space-x-2 px-3 py-2 text-sm"
              >
                {bulkOperation.type === 'approve' && bulkOperation.loading ? (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-ink"></div>
                    <span>Markeren...</span>
                  </>
                ) : (
                  <>
                    <Check className="h-3 w-3" />
                    <span>Markeer als gecontroleerd</span>
                  </>
                )}
              </button>
              <button
                onClick={openBulkRejectModal}
                disabled={bulkOperation.loading}
                className="sf-btn-destructive space-x-2 px-3 py-2 text-sm"
              >
                {bulkOperation.type === 'reject' && bulkOperation.loading ? (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-neon-pink-soft"></div>
                    <span>Depubliceren...</span>
                  </>
                ) : (
                  <>
                    <X className="h-3 w-3" />
                    <span>Depubliceren</span>
                  </>
                )}
              </button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setSelectedEvents(new Set())}
                disabled={bulkOperation.loading}
              >
                Deselecteren
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Events Table */}
      <AdminTable
        columns={columns}
        data={events}
        loading={loading}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        actions={renderActions}
        emptyMessage="Geen evenementen gevonden"
      />

      {/* Pagination */}
      {!loading && events.length > 0 && (
        <div className="mt-6">
          <AdminPagination
            pagination={pagination}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
          />
        </div>
      )}

      {/* Delete/Cancel Confirmation Modal — onderscheid tussen annuleren en definitief verwijderen */}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => !deleteModal.loading && setDeleteModal({ isOpen: false, event: null, mode: 'cancel', loading: false })}
        onConfirm={handleDeleteEvent}
        title={deleteModal.mode === 'hard' ? 'Evenement definitief verwijderen' : 'Evenement annuleren'}
        message={
          deleteModal.mode === 'hard'
            ? `Weet je zeker dat je "${deleteModal.event?.title}" definitief wilt verwijderen? Dit verwijdert permanent het evenement én alle bijbehorende groepen en berichten. Deze actie kan niet ongedaan worden gemaakt.`
            : `Weet je zeker dat je "${deleteModal.event?.title}" wilt annuleren? Het evenement gaat offline en is niet meer zichtbaar voor bezoekers. Bestaande groepen blijven bestaan voor hun leden. Betrokkenen krijgen een notificatie.`
        }
        confirmText={deleteModal.mode === 'hard' ? 'Definitief verwijderen' : 'Evenement annuleren'}
        type={deleteModal.mode === 'hard' ? 'danger' : 'warning'}
        loading={deleteModal.loading}
      />

      {/* Event Detail Modal */}
      <EventDetailModal
        isOpen={eventDetailModal.isOpen}
        onClose={() => setEventDetailModal({ isOpen: false, eventId: '' })}
        eventId={eventDetailModal.eventId}
        onEventUpdated={loadEvents}
      />

      {/* Bulk depubliceren: reden opgeven (geen window.prompt) */}
      <Modal isOpen={bulkRejectModal.isOpen} onClose={closeBulkRejectModal} title="Evenementen depubliceren" size="sm">
        <p className="text-sm text-fg-body mb-4">
          Je staat op het punt om <strong className="text-fg-bright">{selectedEvents.size}</strong> evenement
          {selectedEvents.size !== 1 ? 'en' : ''} offline te halen. De makers zien deze reden en ontvangen hier een e-mail over.
        </p>
        <label className="block text-sm font-semibold text-fg-soft mb-2">
          Reden (optioneel)
        </label>
        <textarea
          value={bulkRejectModal.reason}
          onChange={(e) => setBulkRejectModal(prev => ({ ...prev, reason: e.target.value }))}
          rows={3}
          placeholder="Bijv. ontbrekende informatie, dubbele vermelding, ongepaste inhoud..."
          className="sf-input resize-y"
          disabled={bulkOperation.loading}
        />
        <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={closeBulkRejectModal} disabled={bulkOperation.loading}>
            Annuleren
          </Button>
          <Button variant="danger" onClick={handleBulkReject} loading={bulkOperation.loading}>
            Depubliceren
          </Button>
        </div>
      </Modal>
    </AdminLayout>
  );
};

export default EventManagementPage;